import { LeftOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Spin, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getOrders } from 'api/orders';
import { priceTransformer } from 'utils';

const CALENDAR_STATES_EXCLUDED = '2,6,9,10,12'; // cancelled, completed, stranded, rejected, delivered
const MAX_ORDERS_VISIBLE = 3;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function OrdersCalendar({ filters, dataVersion }) {
  const [currentMonth, setCurrentMonth] = useState(() => dayjs().startOf('month'));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedWeekKey, setExpandedWeekKey] = useState(null);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    const pageSize = 100;
    const baseParams = {
      tz,
      only_scheduled: 1,
      state__not_in: CALENDAR_STATES_EXCLUDED,
      page_size: pageSize,
      ordering: 'scheduled_for',
      scheduled_for_after: currentMonth.startOf('month').format('YYYY-MM-DD'),
      scheduled_for_before: currentMonth.endOf('month').format('YYYY-MM-DD'),
      parent__in: filters.parents?.length ? filters.parents.join(',') : '',
      source__in: filters.sources?.length ? filters.sources.join(',') : '',
      order_types: filters.type || '',
      payment__in: filters.paymentTypes?.length ? filters.paymentTypes.join(',') : '',
    };

    try {
      const allResults = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const { data: { data: { results } } } = await getOrders({ ...baseParams, page });
        const pageResults = results || [];
        allResults.push(...pageResults);
        hasMore = pageResults.length === pageSize;
        page += 1;
      }

      setOrders(allResults);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [tz, currentMonth, filters.parents, filters.sources, filters.type, filters.paymentTypes]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, dataVersion]);

  const monthStart = currentMonth.startOf('month');
  const monthEnd = currentMonth.endOf('month');
  const startDate = monthStart.startOf('week');
  const endDate = monthEnd.endOf('week');

  const ordersByDate = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      const dt = order.scheduled_for || order.requested_pickup_time;
      if (!dt) return;
      const dateKey = dayjs(dt).tz(tz).format('YYYY-MM-DD');
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(order);
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => {
        const ta = dayjs(a.scheduled_for || a.requested_pickup_time).valueOf();
        const tb = dayjs(b.scheduled_for || b.requested_pickup_time).valueOf();
        return ta - tb;
      });
    });
    return map;
  }, [orders, tz]);

  const weeks = useMemo(() => {
    const result = [];
    let d = startDate;
    while (d.isBefore(endDate) || d.isSame(endDate, 'day')) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(d.clone());
        d = d.add(1, 'day');
      }
      result.push(week);
    }
    return result;
  }, [startDate, endDate]);

  const todayKey = dayjs().tz(tz).format('YYYY-MM-DD');

  const formatOrderBlock = (order) => {
    const customerName = order.customer?.name || 'Unknown';
    const dt = order.scheduled_for || order.requested_pickup_time;
    const timeStr = dt ? dayjs(dt).tz(tz).format('h:mm A') : '';
    const total = order.value != null ? priceTransformer(order.value) : '';
    return `${customerName} @ ${timeStr} - ${total}`;
  };

  const getWeekKey = (week) => week[0].format('YYYY-MM-DD');

  if (loading && !error) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex vertical gap="middle" style={{ minHeight: 400 }}>
        <Alert
          message="Failed to load calendar"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={fetchOrders}>
              Retry
            </Button>
          }
        />
      </Flex>
    );
  }

  return (
    <div className="orders-calendar">
      <Flex justify="space-between" align="center" className="mb-4">
        <Flex align="center" gap={8}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => setCurrentMonth((m) => m.subtract(1, 'month'))}
            aria-label="Previous month"
          />
          <Typography.Title level={5} style={{ margin: 0, minWidth: 140, textAlign: 'center' }}>
            {currentMonth.format('MMMM YYYY')}
          </Typography.Title>
          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={() => setCurrentMonth((m) => m.add(1, 'month'))}
            aria-label="Next month"
          />
        </Flex>
      </Flex>

      <div className="orders-calendar-grid">
        <div className="orders-calendar-weekday-headers">
          {WEEKDAYS.map((d) => (
            <div key={d} className="orders-calendar-weekday-cell">
              {d}
            </div>
          ))}
        </div>

        {weeks.map((week) => {
          const weekKey = getWeekKey(week);
          const isExpanded = expandedWeekKey === weekKey;

          return (
            <div
              key={weekKey}
              className={`orders-calendar-week-row ${isExpanded ? 'orders-calendar-week-row--expanded' : ''}`}
            >
              {week.map((date) => {
                const dateKey = date.format('YYYY-MM-DD');
                const isCurrentMonth = date.month() === currentMonth.month();
                const isToday = dateKey === todayKey;
                const dayOrders = ordersByDate[dateKey] || [];

                return (
                  <div
                    key={dateKey}
                    className={`orders-calendar-day-cell ${!isCurrentMonth ? 'orders-calendar-day-cell--other-month' : ''} ${isToday ? 'orders-calendar-day-cell--today' : ''}`}
                  >
                    <div className="orders-calendar-day-number">{date.date()}</div>
                    <div className="orders-calendar-day-orders">
                      {dayOrders.slice(0, MAX_ORDERS_VISIBLE).map((order) => (
                        <Tooltip key={order.id} title={formatOrderBlock(order)}>
                          <Link
                            to={`/console/orders/${order.id}`}
                            className="orders-calendar-order-block"
                          >
                            {formatOrderBlock(order)}
                          </Link>
                        </Tooltip>
                      ))}
                      {dayOrders.length > MAX_ORDERS_VISIBLE && (
                        <button
                          type="button"
                          className="orders-calendar-view-more"
                          onClick={() => setExpandedWeekKey((k) => (k === weekKey ? null : weekKey))}
                        >
                          <DownOutlined className={isExpanded ? 'orders-calendar-view-more-icon--rotated' : ''} />
                          {' '}
                          {isExpanded
                            ? 'View less'
                            : `View ${dayOrders.length - MAX_ORDERS_VISIBLE} more orders`}
                        </button>
                      )}
                      {isExpanded && dayOrders.slice(MAX_ORDERS_VISIBLE).map((order) => (
                        <Tooltip key={order.id} title={formatOrderBlock(order)}>
                          <Link
                            to={`/console/orders/${order.id}`}
                            className="orders-calendar-order-block"
                          >
                            {formatOrderBlock(order)}
                          </Link>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <style>{`
        .orders-calendar-grid {
          border: 1px solid #f0f0f0;
          border-radius: 8px;
          overflow: hidden;
        }
        .orders-calendar-weekday-headers {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          background: #fafafa;
          font-weight: 600;
          font-size: 12px;
          color: #666;
        }
        .orders-calendar-weekday-cell {
          padding: 8px;
          text-align: center;
          border-bottom: 1px solid #f0f0f0;
        }
        .orders-calendar-week-row {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          min-height: 100px;
        }
        .orders-calendar-week-row--expanded {
          min-height: auto;
        }
        .orders-calendar-day-cell {
          border-right: 1px solid #f0f0f0;
          border-bottom: 1px solid #f0f0f0;
          padding: 4px;
          min-height: 100px;
          background: #fff;
        }
        .orders-calendar-day-cell:nth-child(7n) {
          border-right: none;
        }
        .orders-calendar-day-cell--other-month {
          background: #fafafa;
          color: #bbb;
        }
        .orders-calendar-day-cell--today {
          background: #e6f7ff;
          border: 2px solid #1890ff;
        }
        .orders-calendar-day-number {
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 4px;
        }
        .orders-calendar-day-orders {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .orders-calendar-order-block {
          font-size: 11px;
          padding: 4px 6px;
          background: #f5f5f5;
          border-radius: 4px;
          cursor: pointer;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
          color: inherit;
          text-decoration: none;
        }
        .orders-calendar-order-block:hover {
          background: #e6f7ff;
          color: inherit;
        }
        .orders-calendar-view-more {
          font-size: 11px;
          padding: 4px 6px;
          background: transparent;
          border: none;
          color: #1890ff;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .orders-calendar-view-more:hover {
          text-decoration: underline;
        }
        .orders-calendar-view-more-icon--rotated {
          transform: rotate(180deg);
        }
      `}</style>
    </div>
  );
}

export default OrdersCalendar;
